// CRM Phase 3 — Nurture Drip Campaigns
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Play, Pause, Trash2, Mail, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY_STEP = { order: 1, delay_hours: 24, subject: "", body_html: "", channel: "email" };

export default function CrmNurture() {
  const [seqs, setSeqs] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ name: "", active: true, trigger_type: "qualification", trigger_value: "MQL", steps: [ { ...EMPTY_STEP } ] });

  const load = useCallback(async () => {
    const [s, e] = await Promise.all([
      api.get("/crm/nurture/sequences"),
      api.get("/crm/nurture/enrolments"),
    ]);
    setSeqs(s.data); setEnrolments(e.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, { ...EMPTY_STEP, order: f.steps.length + 1 }] }));
  const rmStep = (i) => setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 })) }));
  const updStep = (i, patch) => setForm(f => ({ ...f, steps: f.steps.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));

  const save = async () => {
    if (!form.name || form.steps.length === 0) { toast.error("Add a name and at least one step"); return; }
    try {
      await api.post("/crm/nurture/sequences", form);
      toast.success("Sequence created");
      setDlg(false); setForm({ name: "", active: true, trigger_type: "qualification", trigger_value: "MQL", steps: [{ ...EMPTY_STEP }] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggle = async (s) => {
    await api.put(`/crm/nurture/sequences/${s.id}`, { ...s, active: !s.active });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete sequence and its enrolments?")) return;
    await api.delete(`/crm/nurture/sequences/${id}`);
    load();
  };

  const pauseEnr = async (eid, action) => {
    await api.post(`/crm/nurture/enrolments/${eid}/${action}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="crm-nurture-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl">Drip Sequences</h2>
          <p className="text-sm text-muted-foreground mt-1">Multi-step email nurture campaigns triggered by lead qualification, stage or manual enrolment.</p>
        </div>
        <Button onClick={() => setDlg(true)} data-testid="nurture-new-btn"><Plus className="w-4 h-4 mr-1" /> New sequence</Button>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {seqs.length === 0 && <div className="text-sm text-muted-foreground bg-card border border-border rounded-2xl p-8 text-center">No sequences yet. Create your first drip campaign.</div>}
        {seqs.map(s => (
          <div key={s.id} data-testid={`nurture-seq-${s.id}`} className="bg-card border border-border rounded-2xl p-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium">{s.name}</div>
                <span className={`text-[10px] overline px-2 py-0.5 rounded-full ${s.active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{s.active ? "Active" : "Paused"}</span>
                <span className="text-[10px] overline px-2 py-0.5 rounded-full bg-secondary/10 text-secondary">
                  <Zap className="w-3 h-3 inline mr-0.5" />{s.trigger_type}{s.trigger_value ? ` · ${s.trigger_value}` : ""}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Mail className="w-3 h-3" /> {s.steps?.length || 0} step(s)</div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => toggle(s)} data-testid={`nurture-toggle-${s.id}`}>{s.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</Button>
              <Button variant="ghost" size="sm" onClick={() => del(s.id)} data-testid={`nurture-delete-${s.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </div>

      {enrolments.length > 0 && (
        <div className="mt-8">
          <h3 className="font-display text-lg">Active enrolments ({enrolments.length})</h3>
          <div className="mt-3 space-y-2">
            {enrolments.slice(0, 10).map(e => (
              <div key={e.id} className="bg-card border border-border rounded-xl px-4 py-3 flex items-center justify-between text-sm">
                <div>
                  <span className="font-mono text-xs">{e.lead_id.slice(0, 8)}</span>
                  <span className="mx-2 text-muted-foreground">·</span>
                  Step {e.current_step + 1} · {e.status}
                </div>
                <div className="flex items-center gap-1">
                  {e.status === "active" && <Button variant="ghost" size="sm" onClick={() => pauseEnr(e.id, "pause")}><Pause className="w-3.5 h-3.5" /></Button>}
                  {e.status === "paused" && <Button variant="ghost" size="sm" onClick={() => pauseEnr(e.id, "resume")}><Play className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New drip sequence</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input data-testid="nurture-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Q1 Follow-up campaign" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Trigger</Label>
                <Select value={form.trigger_type} onValueChange={v => setForm({ ...form, trigger_type: v })}>
                  <SelectTrigger data-testid="nurture-trigger-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="qualification">Lead qualification</SelectItem>
                    <SelectItem value="stage">Deal stage</SelectItem>
                    <SelectItem value="manual">Manual only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.trigger_type !== "manual" && (
                <div>
                  <Label>Trigger value</Label>
                  <Input value={form.trigger_value || ""} onChange={e => setForm({ ...form, trigger_value: e.target.value })} placeholder="MQL / SQL / proposal" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Steps ({form.steps.length})</Label>
                <Button size="sm" variant="outline" onClick={addStep} data-testid="nurture-add-step-btn">Add step</Button>
              </div>
              <div className="mt-2 space-y-3">
                {form.steps.map((s, i) => (
                  <div key={i} className="border border-border rounded-lg p-3 space-y-2" data-testid={`nurture-step-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center">{i + 1}</span>
                      <Input type="number" min={0} value={s.delay_hours} onChange={e => updStep(i, { delay_hours: parseInt(e.target.value || 0, 10) })} className="w-28" placeholder="Delay hours" />
                      <span className="text-xs text-muted-foreground">hours after previous</span>
                      {form.steps.length > 1 && <Button variant="ghost" size="sm" onClick={() => rmStep(i)} className="ml-auto"><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                    </div>
                    <Input value={s.subject} onChange={e => updStep(i, { subject: e.target.value })} placeholder="Email subject" />
                    <Textarea rows={3} value={s.body_html} onChange={e => updStep(i, { body_html: e.target.value })} placeholder="HTML body (Hi {{name}}, ...)" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg(false)}>Cancel</Button>
            <Button onClick={save} data-testid="nurture-save-btn">Create sequence</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
